<!-- ====================================================== -->
<!-- PAGE HEADER -->
<!-- ====================================================== -->

<div
  class="report-page-header flex items-center justify-between px-2"
>
  <h3 class="text-lg font-semibold text-[#1E2A78]">
    {{ 'CREDIT_REPORT.TITLE' | translate }}
  </h3>

  <div class="flex items-center gap-3">
    <button
      *ngIf="!loading && !errorMessage && reportSections.length > 0"
      type="button"
      [disabled]="exporting"
      (click)="downloadWordReport()"
      class="inline-flex items-center gap-2 rounded-lg bg-[#1E2A78] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#26358f] disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span *ngIf="!exporting">{{ 'CREDIT_REPORT.DOWNLOAD_WORD' | translate }}</span>
      <span *ngIf="exporting">{{ 'CREDIT_REPORT.GENERATING' | translate }}</span>
    </button>

    <app-back-button></app-back-button>
  </div>
</div>

<!-- ====================================================== -->
<!-- LOADING -->
<!-- ====================================================== -->

<div
  *ngIf="loading"
  class="my-8 rounded-xl border border-gray-200 bg-gray-50 p-10 text-center text-gray-500"
>
  {{ 'CREDIT_REPORT.LOADING' | translate }}
</div>

<!-- ====================================================== -->
<!-- ERROR -->
<!-- ====================================================== -->

<div
  *ngIf="!loading && errorMessage"
  class="my-6 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
>
  {{ errorMessage | translate }}
</div>

<!-- ====================================================== -->
<!-- REPORT -->
<!-- ====================================================== -->

<div
  *ngIf="
    !loading &&
    !errorMessage &&
    reportSections.length > 0;
    else noData
  "
  class="credit-report"
>
  <section
    *ngFor="
      let section of reportSections;
      trackBy: trackBySection
    "
    class="report-section"
  >
    <!-- Introduction opens the Credit Proposal on a new page (as in the Word report). -->
    @if (+section.refValSeq === introductionRefValSeq) {
      <div class="credit-proposal-title">
        @if (institutionName) {
          <div class="credit-proposal-client">{{ institutionName }}</div>
        }
        <div>Credit Proposal</div>
      </div>
    }

    <!-- Title from Picklist Group 28; "2.1"-style labels are indented sub-sections. -->
    @let headingLabel = sectionHeadingLabels.bySection.get(+section.refValSeq);
    <h2
      *ngIf="section.title"
      class="section-title"
      [class.sub-section-title]="isSubSectionLabel(headingLabel)"
    >
      @if (headingLabel) {
        {{ headingLabel }} {{ section.title }}:
      } @else {
        {{ section.title }}
      }
    </h2>

    <!-- ================================================== -->
    <!-- REF-VAL-SEQ MAPPED SECTIONS (explicit, not blkTyp)  -->
    <!-- Financial Analysis Details (2647), Repayment Plan   -->
    <!-- (2645), Disbursement & Repayment Summary (2646)     -->
    <!-- ================================================== -->

    <ng-container
      *ngIf="
        isFinancialAnalysisDetailsSection(section);
        else notFinancialAnalysisDetails
      "
    >
      <ng-container
        *ngIf="
          getFinancialAnalysisDetailsData(section)
            as financialAnalysisData;
          else noFinancialAnalysisDetailsData
        "
      >
        <!-- Same layout as the Word report's Institutional Highlights table. -->
        <div
          *ngIf="
            financialAnalysisData.rows.length > 0;
            else noFinancialAnalysisRows
          "
          class="table-wrapper overflow-x-auto"
        >
          @let highlights = getInstitutionalHighlightsView(financialAnalysisData);
          <table class="financial-analysis-report-table institutional-highlights-table" dir="ltr" data-export-table="financial-analysis-details">
            <thead>
              @if (highlights.groups) {
                <tr>
                  <th rowspan="2" class="indicator-column">Indicators</th>
                  @for (group of highlights.groups; track $index) {
                    <th [attr.colspan]="group.span" class="group-header">{{ group.label }}</th>
                  }
                  @if (highlights.showCagr) {
                    <th rowspan="2" class="cagr-column">CAGR</th>
                  }
                </tr>
                <tr>
                  @for (period of highlights.periods; track period.column) {
                    <th class="period-column">{{ period.label }}</th>
                  }
                </tr>
              } @else {
                <tr>
                  <th class="indicator-column">Indicators</th>
                  @for (period of highlights.periods; track period.column) {
                    <th class="period-column">{{ period.label }}</th>
                  }
                  @if (highlights.showCagr) {
                    <th class="cagr-column">CAGR</th>
                  }
                </tr>
              }
            </thead>

            <tbody>
              @for (row of highlights.rows; track $index) {
                <tr>
                  <td>{{ row.indicator }}</td>
                  @for (value of row.values; track $index) {
                    <td class="text-end">
                      @if (value !== null) {
                        {{ value | number: '1.0-2' }}
                      } @else {
                        -
                      }
                    </td>
                  }
                  @if (highlights.showCagr) {
                    <td class="text-end">{{ row.cagr }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>

        <ng-template #noFinancialAnalysisRows>
          <div class="empty-section-message">
            {{ 'CREDIT_REPORT.NO_FINANCIAL_ANALYSIS_ROWS' | translate }}
          </div>
        </ng-template>
      </ng-container>

      <ng-template #noFinancialAnalysisDetailsData>
        <div class="empty-section-message">
          {{ 'CREDIT_REPORT.NO_FINANCIAL_ANALYSIS_DATA' | translate }}
        </div>
      </ng-template>
    </ng-container>

    <ng-template #notFinancialAnalysisDetails>
      <ng-container
        *ngIf="
          isRepaymentPlanSection(section);
          else notRepaymentPlan
        "
      >
        <ng-container
          *ngIf="
            getRepaymentPlanData(section) as repaymentPlanData;
            else noRepaymentPlanData
          "
        >
          <div
            *ngIf="
              repaymentPlanData.rows.length > 0;
              else noRepaymentPlanRows
            "
            class="table-wrapper overflow-x-auto repayment-plan-landscape"
          >
            <!-- One schedule per repayment term type (e.g. Conventional / Islamic). -->
            @for (
              group of getRepaymentPlanTypeGroups(repaymentPlanData);
              track group.type;
              let lastGroup = $last
            ) {
              <table class="repayment-plan-report-table" dir="ltr" data-export-table="repayment-plan">
                <thead>
                  @if (group.type) {
                    <tr>
                      <th
                        [attr.colspan]="group.data.columns.length + 4"
                        class="type-header"
                      >
                        {{ group.type }}
                      </th>
                    </tr>
                  }

                  <tr>
                    <th colspan="3" class="group-header">
                      Disbursements
                    </th>
                    <th
                      [attr.colspan]="group.data.columns.length + 1"
                      class="group-header text-center"
                    >
                      Repayment Schedule
                    </th>
                  </tr>

                  <tr>
                    <th>Quarter</th>
                    <th>Month</th>
                    <th class="text-end">Tranche Amount</th>
                    @for (column of group.data.columns; track column) {
                      <th class="text-end" [title]="column">
                        {{ formatRepaymentPeriod(column) }}
                      </th>
                    }
                    <th class="text-end total-column">Total</th>
                  </tr>
                </thead>

                <tbody>
                  @if (group.showOpeningExposure) {
                    <tr class="opening-row">
                      <td colspan="2" class="font-semibold">
                        Opening Exposure
                      </td>
                      <td class="text-end font-semibold">
                        {{ formatAmount(group.data.openingExposure) }}
                      </td>
                      @for (column of group.data.columns; track column) {
                        <td></td>
                      }
                      <td></td>
                    </tr>
                  }

                  @for (row of group.data.rows; track $index) {
                    <tr>
                      <td>{{ row.quarter || '-' }}</td>
                      <td>{{ formatRepaymentPeriod(row.month) }}</td>
                      <td class="text-end">
                        {{ formatAmount(row.trancheAmount) }}
                      </td>

                      @for (column of group.data.columns; track column) {
                        <td class="text-end">
                          @if (hasRepaymentInstallment(row, column)) {
                            {{ formatAmount(row.schedule[column]) }}
                          } @else {
                            -
                          }
                        </td>
                      }

                      <td class="text-end font-semibold">
                        {{ formatAmount(getRepaymentRowTotal(row)) }}
                      </td>
                    </tr>
                  }

                  <tr class="total-row">
                    <td colspan="3" class="font-bold">Total</td>
                    @for (column of group.data.columns; track column) {
                      <td class="text-end font-bold">
                        {{ formatAmount(getRepaymentColumnTotal(group.data, column)) }}
                      </td>
                    }
                    <td class="text-end font-bold">
                      {{ formatAmount(group.data.grandTotal) }}
                    </td>
                  </tr>

                  <!-- Existing repayments / disbursements are proposal-level: last table only. -->
                  @if (lastGroup) {
                    <tr class="blank-row">
                      <td [attr.colspan]="group.data.columns.length + 4"></td>
                    </tr>
                    <tr class="existing-repayments-row">
                      <td colspan="3" class="font-bold">
                        Existing Repayments
                      </td>
                      @for (column of group.data.columns; track column) {
                        <td></td>
                      }
                      <td class="text-end font-bold">
                        {{ formatAmount(existingRepayments) }}
                      </td>
                    </tr>
                    <tr class="existing-disbursements-row">
                      <td colspan="3" class="font-bold">
                        Existing Disbursements
                      </td>
                      @for (column of group.data.columns; track column) {
                        <td></td>
                      }
                      <td class="text-end font-bold">
                        {{ formatAmount(existingDisbursements) }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            }

            <div class="repayment-plan-caption">
              {{ 'CREDIT_REPORT.REPAYMENT_PLAN_CAPTION' | translate }}
            </div>

            <!-- One consolidated schedule; the Type column carries Conventional / Islamic. -->
            <table class="repayment-plan-report-table" dir="ltr" data-export-table="repayment-plan">
              <thead>
                <tr>
                  <th colspan="4" class="group-header">
                    Disbursements
                  </th>
                  <th
                    [attr.colspan]="repaymentPlanData.columns.length + 1"
                    class="group-header text-center"
                  >
                    Repayment Schedule
                  </th>
                </tr>

                <tr>
                  <th>Quarter</th>
                  <th>Type</th>
                  <th>Month</th>
                  <th class="text-end">Tranche Amount</th>
                  @for (column of repaymentPlanData.columns; track column) {
                    <th class="text-end" [title]="column">
                      {{ formatRepaymentPeriod(column) }}
                    </th>
                  }
                  <th class="text-end total-column">Total</th>
                </tr>
              </thead>

              <tbody>
                <tr class="opening-row">
                  <td colspan="3" class="font-semibold">
                    Opening Exposure
                  </td>
                  <td class="text-end font-semibold">
                    {{ formatAmount(repaymentPlanData.openingExposure) }}
                  </td>
                  @for (column of repaymentPlanData.columns; track column) {
                    <td></td>
                  }
                  <td></td>
                </tr>

                @for (row of repaymentPlanData.rows; track $index) {
                  <tr>
                    <td>{{ row.quarter || '-' }}</td>
                    <td>{{ row.type || '-' }}</td>
                    <td>{{ formatRepaymentPeriod(row.month) }}</td>
                    <td class="text-end">
                      {{ formatAmount(row.trancheAmount) }}
                    </td>

                    @for (column of repaymentPlanData.columns; track column) {
                      <td class="text-end">
                        @if (hasRepaymentInstallment(row, column)) {
                          {{ formatAmount(row.schedule[column]) }}
                        } @else {
                          -
                        }
                      </td>
                    }

                    <td class="text-end font-semibold">
                      {{ formatAmount(getRepaymentRowTotal(row)) }}
                    </td>
                  </tr>
                }

                <tr class="total-row">
                  <td colspan="4" class="font-bold">Total</td>
                  @for (column of repaymentPlanData.columns; track column) {
                    <td class="text-end font-bold">
                      {{ formatAmount(getRepaymentColumnTotal(repaymentPlanData, column)) }}
                    </td>
                  }
                  <td class="text-end font-bold">
                    {{ formatAmount(repaymentPlanData.grandTotal) }}
                  </td>
                </tr>

                <tr class="blank-row">
                  <td [attr.colspan]="repaymentPlanData.columns.length + 5"></td>
                </tr>
                <tr class="existing-repayments-row">
                  <td colspan="4" class="font-bold">
                    Existing Repayments
                  </td>
                  @for (column of repaymentPlanData.columns; track column) {
                    <td></td>
                  }
                  <td class="text-end font-bold">
                    {{ formatAmount(existingRepayments) }}
                  </td>
                </tr>
                <tr class="existing-disbursements-row">
                  <td colspan="4" class="font-bold">
                    Existing Disbursements
                  </td>
                  @for (column of repaymentPlanData.columns; track column) {
                    <td></td>
                  }
                  <td class="text-end font-bold">
                    {{ formatAmount(existingDisbursements) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="report-total-summary">
            {{
              'CREDIT_REPORT.TOTAL_SUMMARY'
                | translate
                  : {
                      disbursement: formatAmount(repaymentPlanData.totalDisbursementAmount),
                      repayment: formatAmount(repaymentPlanData.grandTotal)
                    }
            }}
          </p>

          <ng-template #noRepaymentPlanRows>
            <div class="empty-section-message">
              {{ 'CREDIT_REPORT.NO_REPAYMENT_PLAN_ROWS' | translate }}
            </div>
          </ng-template>
        </ng-container>

        <ng-template #noRepaymentPlanData>
          <div class="empty-section-message">
            {{ 'CREDIT_REPORT.NO_REPAYMENT_PLAN_DATA' | translate }}
          </div>
        </ng-template>
      </ng-container>

      <ng-template #notRepaymentPlan>
        <ng-container
          *ngIf="
            isDisbursementRepaymentSummarySection(section);
            else notDisbursementSummary
          "
        >
          <ng-container
            *ngIf="
              getDisbursementSummaryGroups(section)
                as summaryGroups;
              else noDisbursementSummaryData
            "
          >
            <div
              *ngIf="
                summaryGroups.length > 0;
                else noDisbursementSummaryRows
              "
              class="table-wrapper overflow-x-auto"
            >
              <!-- One table per repayment term type (e.g. Conventional / Islamic). -->
              @for (group of summaryGroups; track group.type) {
              @let summaryData = group.data;
              <table class="disbursement-summary-report-table" dir="ltr" data-export-table="disbursement-summary">
                <thead>
                  @if (group.type) {
                    <tr>
                      <th colspan="6" class="type-header">
                        {{ group.type }}
                      </th>
                    </tr>
                  }

                  <tr>
                    <th>Month</th>
                    <th>Proposed<br />Disbursements</th>
                    <th>Proposed Repayments</th>
                    <th>
                      Scheduled Disbursements<br />
                      <span class="subtext">(Existing Agreements)</span>
                    </th>
                    <th>
                      Scheduled Repayments<br />
                      <span class="subtext">(Existing Agreements)</span>
                    </th>
                    <th>
                      Maximum Exposure<br />at any Point of Time
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr class="opening-row">
                    <td class="font-bold">Opening</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td class="text-end font-bold">
                      {{
                        summaryData.openingExposure
                          | number: '1.0-2'
                      }}
                    </td>
                  </tr>

                  <tr
                    *ngFor="
                      let row of summaryData.rows;
                      trackBy: trackBySummaryRow
                    "
                  >
                    <td class="font-semibold">
                      {{ formatSummaryMonth(row.month) }}
                    </td>

                    <td class="text-end">
                      <ng-container *ngIf="row.proposedDisbursement > 0; else summaryEmptyValue">
                        {{ row.proposedDisbursement | number: '1.0-2' }}
                      </ng-container>
                    </td>

                    <td class="text-end">
                      <ng-container *ngIf="row.proposedRepayment > 0; else summaryEmptyValue">
                        {{ row.proposedRepayment | number: '1.0-2' }}
                      </ng-container>
                    </td>

                    <td class="text-end">
                      <ng-container *ngIf="row.scheduledDisbursement > 0; else summaryEmptyValue">
                        {{ row.scheduledDisbursement | number: '1.0-2' }}
                      </ng-container>
                    </td>

                    <td class="text-end">
                      <ng-container *ngIf="row.scheduledRepayment > 0; else summaryEmptyValue">
                        {{ row.scheduledRepayment | number: '1.0-2' }}
                      </ng-container>
                    </td>

                    <td class="text-end font-semibold">
                      {{ row.maximumExposure | number: '1.0-2' }}
                    </td>
                  </tr>

                  <tr class="total-row">
                    <td class="font-bold">Total</td>
                    <td class="text-end font-bold">
                      {{ summaryData.totals.proposedDisbursement | number: '1.0-2' }}
                    </td>
                    <td class="text-end font-bold">
                      {{ summaryData.totals.proposedRepayment | number: '1.0-2' }}
                    </td>
                    <td class="text-end font-bold">
                      {{ summaryData.totals.scheduledDisbursement | number: '1.0-2' }}
                    </td>
                    <td class="text-end font-bold">
                      {{ summaryData.totals.scheduledRepayment | number: '1.0-2' }}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              }

              <ng-template #summaryEmptyValue>-</ng-template>
            </div>

            <ng-template #noDisbursementSummaryRows>
              <div class="empty-section-message">
                {{ 'CREDIT_REPORT.NO_SUMMARY_ROWS' | translate }}
              </div>
            </ng-template>
          </ng-container>

          <ng-template #noDisbursementSummaryData>
            <div class="empty-section-message">
              {{ 'CREDIT_REPORT.NO_SUMMARY_DATA' | translate }}
            </div>
          </ng-template>
        </ng-container>

        <ng-template #notDisbursementSummary>
    <!-- ================================================== -->
    <!-- RICH / NESTED REPORT SECTION -->
    <!-- Financial Performance, Borrower Review, etc. -->
    <!-- ================================================== -->

    <ng-container
      *ngIf="
        hasNestedReportBlocks(section);
        else otherSectionTypes
      "
    >
      <ng-container
        *ngFor="
          let contentBlock of
            getNestedReportBlocks(section);
          trackBy: trackByNestedBlock
        "
      >
        <!-- Heading -->
        <h3
          *ngIf="
            contentBlock.type === 'heading' &&
            showNestedBlockTitle(
              section,
              contentBlock
            )
          "
          class="mb-4 mt-6 border-b border-gray-200 pb-2 text-xl font-semibold text-[#1E2A78]"
        >
          {{ contentBlock.title }}
        </h3>

        <!-- Paragraph -->
        <ng-container
          *ngIf="
            contentBlock.type === 'paragraph'
          "
        >
          <h3
            *ngIf="
              showNestedBlockTitle(
                section,
                contentBlock
              )
            "
            class="mb-3 mt-6 text-lg font-semibold text-[#1E2A78]"
          >
            {{ contentBlock.title }}
          </h3>

          <div
            *ngIf="contentBlock.content"
            class="normal-paragraph rich-text-content"
            [innerHTML]="contentBlock.content | richText"
          ></div>
        </ng-container>

        <!-- Bullet List -->
        <ng-container
          *ngIf="
            contentBlock.type === 'bullet-list'
          "
        >
          <h3
            *ngIf="
              showNestedBlockTitle(
                section,
                contentBlock
              )
            "
            class="mb-3 mt-6 text-lg font-semibold text-[#1E2A78]"
          >
            {{ contentBlock.title }}
          </h3>

          <ul class="report-list bullet-list">
            <li
              *ngFor="
                let item of
                  getNestedBlockLines(
                    contentBlock.content
                  )
              "
            >
              {{ item }}
            </li>
          </ul>
        </ng-container>

        <!-- Numbered List -->
        <ng-container
          *ngIf="
            contentBlock.type === 'numbered-list'
          "
        >
          <h3
            *ngIf="
              showNestedBlockTitle(
                section,
                contentBlock
              )
            "
            class="mb-3 mt-6 text-lg font-semibold text-[#1E2A78]"
          >
            {{ contentBlock.title }}
          </h3>

          <ol class="report-list numbered-list">
            <li
              *ngFor="
                let item of
                  getNestedBlockLines(
                    contentBlock.content
                  )
              "
            >
              {{ removeExistingNumber(item) }}
            </li>
          </ol>
        </ng-container>

        <!-- Table -->
        <ng-container
          *ngIf="
            contentBlock.type === 'table'
          "
        >
          <ng-container
            *ngIf="
              contentBlock.table as table
            "
          >
            <h3
              *ngIf="
                showNestedBlockTitle(
                  section,
                  contentBlock
                )
              "
              class="mb-3 mt-6 text-base font-semibold text-[#1E2A78]"
            >
              {{ contentBlock.title }}
            </h3>

            <div class="table-wrapper overflow-x-auto">
              <table
                class="w-full min-w-[850px] border-collapse"
              >
                <thead>
                  <!-- Main Header -->
                  <tr class="bg-[#1E2A78] text-white">
                    <th
                      *ngFor="
                        let column of table.columns
                      "
                      [attr.colspan]="
                        getColumnColspan(column)
                      "
                      [attr.rowspan]="
                        getColumnRowspan(
                          table,
                          column
                        )
                      "
                      class="border border-indigo-400 px-4 py-3 text-center text-sm font-semibold"
                    >
                      {{ column.title }}
                    </th>
                  </tr>

                  <!-- Sub Headers -->
                  <tr
                    *ngIf="
                      hasTableSubColumns(table)
                    "
                    class="bg-[#26358f] text-white"
                  >
                    <ng-container
                      *ngFor="
                        let column of table.columns
                      "
                    >
                      <th
                        *ngFor="
                          let subColumn of
                            getSubColumns(column)
                        "
                        class="border border-indigo-400 px-4 py-2 text-center text-sm font-medium"
                      >
                        {{ subColumn }}
                      </th>
                    </ng-container>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    *ngFor="
                      let row of table.rows;
                      let rowIndex = index
                    "
                    class="border-b border-gray-200"
                    [class.bg-gray-50]="
                      rowIndex % 2 === 1
                    "
                  >
                    <td
                      *ngFor="let cell of row; let cellIndex = index"
                      class="border border-gray-200 px-4 py-3 align-top text-sm text-gray-700"
                      [class.text-end]="(cell | tableCell: table.columns : cellIndex).numeric"
                      [class.tabular-nums]="(cell | tableCell: table.columns : cellIndex).numeric"
                    >
                      {{ (cell | tableCell: table.columns : cellIndex).text || '-' }}
                    </td>
                  </tr>

                  <tr
                    *ngIf="
                      !table.rows ||
                      table.rows.length === 0
                    "
                  >
                    <td
                      [attr.colspan]="
                        getPhysicalColumnCount(table)
                      "
                      class="border border-gray-200 px-4 py-8 text-center text-gray-500"
                    >
                      {{ 'CREDIT_REPORT.NO_TABLE_DATA' | translate }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p
              *ngIf="contentBlock.content"
              class="mt-3 whitespace-pre-line text-sm leading-6 text-gray-600"
            >
              {{ contentBlock.content }}
            </p>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>

    <!-- ================================================== -->
    <!-- OTHER SECTION TYPES -->
    <!-- This template runs only when nested blocks do not exist -->
    <!-- ================================================== -->

    <ng-template #otherSectionTypes>
      <!-- ================================================ -->
      <!-- CREDIT RECOMMENDATION -->
      <!-- ================================================ -->

      <ng-container
        *ngIf="
          isRecommendationSection(section);
          else nonRecommendationSection
        "
      >
        <div
          *ngIf="
            getRecommendationRows(section)
              .length > 0;
            else noRecommendationData
          "
          class="table-wrapper"
        >
          <table class="recommendation-table" dir="ltr" data-export-table="recommendation">
            <thead>
              <tr>
                <th class="serial-column">
                  Sr.
                </th>

                <th class="description-column">
                  Description
                </th>

                <th class="recommendation-column">
                  Recommendation
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                *ngFor="
                  let row of
                    getRecommendationRows(
                      section
                    )
                "
              >
                <td class="serial-cell">
                  {{ row.serialNo }}
                </td>

                <td class="description-cell">
                  {{ row.description }}
                </td>

                <td class="recommendation-cell">
                  <ng-container
                    *ngFor="
                      let recommendation of
                        getRecommendationBlocks(
                          row
                        )
                    "
                  >
                    <!-- Bullet List -->
                    <ul
                      *ngIf="
                        recommendation.type ===
                        'bullet-list'
                      "
                      class="report-list bullet-list"
                    >
                      <li
                        *ngFor="
                          let recommendationItem of
                            getRecommendationItems(
                              recommendation
                            )
                        "
                      >
                        {{ recommendationItem }}
                      </li>
                    </ul>

                    <!-- Numbered List -->
                    <ol
                      *ngIf="
                        recommendation.type ===
                        'numbered-list'
                      "
                      class="report-list scoped-numbered-list"
                    >
                      <li
                        *ngFor="
                          let recommendationItem of
                            getRecommendationItems(
                              recommendation
                            );
                          let itemIndex = index
                        "
                      >
                        <span class="scoped-number">{{
                          getRecommendationNumber(
                            row,
                            recommendation,
                            itemIndex
                          )
                        }}</span>
                        <span>{{
                          removeExistingNumber(
                            recommendationItem
                          )
                        }}</span>
                      </li>
                    </ol>

                    <!-- Paragraph -->
                    <div
                      *ngIf="
                        recommendation.type ===
                          'paragraph' &&
                        recommendation.content
                      "
                      class="recommendation-paragraph rich-text-content"
                      [innerHTML]="recommendation.content | richText"
                    ></div>
                  </ng-container>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #noRecommendationData>
          <div class="empty-section-message">
            {{ 'CREDIT_REPORT.NO_RECOMMENDATION_DATA' | translate }}
          </div>
        </ng-template>
      </ng-container>

      <!-- ================================================ -->
      <!-- NON-RECOMMENDATION SECTION -->
      <!-- ================================================ -->

      <ng-template #nonRecommendationSection>
        <!-- ============================================== -->
        <!-- INSTITUTIONAL RATING -->
        <!-- ============================================== -->

        <ng-container
          *ngIf="
            isInstitutionalRatingSection(section);
            else regularSection
          "
        >
          <ng-container
            *ngIf="
              getInstitutionalRatingData(section)
                as ratingData;
              else noInstitutionalRatingData
            "
          >
            <div
              *ngIf="ratingData.introduction"
              class="section-introduction rich-text-content"
              [innerHTML]="ratingData.introduction | richText"
            ></div>

            <div class="table-wrapper">
              <table class="rating-table" dir="ltr" data-export-table="institutional-rating">
                <thead>
                  <tr>
                    <th
                      rowspan="2"
                      class="category-column"
                    >
                      Category
                    </th>

                    <th
                      rowspan="2"
                      class="weight-column text-center"
                    >
                      Weight
                    </th>

                    <th
                      colspan="2"
                      class="rating-group-header"
                    >
                      Current Rating

                      <span class="rating-year">
                        June 2024
                      </span>
                    </th>

                    <th
                      colspan="2"
                      class="rating-group-header"
                    >
                      Previous Rating

                      <span class="rating-year">
                        June 2023
                      </span>
                    </th>
                  </tr>

                  <tr>
                    <th class="score-column">
                      Score
                    </th>

                    <th class="rating-column">
                      Rating
                    </th>

                    <th class="score-column">
                      Score
                    </th>

                    <th class="rating-column">
                      Rating
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    *ngFor="
                      let row of
                        getInstitutionalRatingRows(
                          ratingData
                        )
                    "
                  >
                    <td class="category-cell">
                      {{ row.category }}
                    </td>

                    <td class="text-center">
                      {{ row.weight }}
                    </td>

                    <td class="text-center">
                      {{ row.currentScore }}
                    </td>

                    <td class="rating-value text-center">
                      {{ row.currentRating }}
                    </td>

                    <td class="text-center">
                      {{ row.previousScore }}
                    </td>

                    <td class="rating-value text-center">
                      {{ row.previousRating }}
                    </td>
                  </tr>

                  <tr
                    *ngIf="ratingData.finalScore"
                    class="final-score-row"
                  >
                    <td class="final-score-title">
                      {{
                        ratingData.finalScore.title
                      }}
                    </td>

                    <td></td>

                    <td class="text-center">
                      {{
                        ratingData.finalScore
                          .currentScore
                      }}
                    </td>

                    <td class="text-center">
                      {{
                        ratingData.finalScore
                          .currentRating
                      }}
                    </td>

                    <td class="text-center">
                      {{
                        ratingData.finalScore
                          .previousScore
                      }}
                    </td>

                    <td class="text-center">
                      {{
                        ratingData.finalScore
                          .previousRating
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              *ngIf="ratingData.conclusion"
              class="rating-conclusion rich-text-content"
              [innerHTML]="ratingData.conclusion | richText"
            ></div>
          </ng-container>

          <ng-template #noInstitutionalRatingData>
            <div class="empty-section-message">
              {{ 'CREDIT_REPORT.NO_RATING_DATA' | translate }}
            </div>
          </ng-template>
        </ng-container>

        <!-- ============================================== -->
        <!-- REGULAR SECTIONS -->
        <!-- ============================================== -->

        <ng-template #regularSection>
          <ng-container
            *ngFor="
              let block of section.blocks;
              trackBy: trackByBlock
            "
          >
            <!-- Block Type 3: Highlights -->
            <ng-container
              *ngIf="isBlockType(block, 3)"
            >
              <div
                *ngIf="
                  block.jsonData.length > 0
                "
                class="highlight-list"
              >
                <div
                  *ngFor="
                    let item of block.jsonData
                  "
                  class="highlight-item"
                >
                  <span
                    *ngIf="item.showBullet"
                    class="highlight-bullet"
                  >
                    •
                  </span>

                  <div class="highlight-content">
                    <span
                      *ngIf="showHighlightItemTitle(section, item)"
                      class="highlight-title"
                    >
                      {{ item.title }}:
                    </span>

                    <span
                      *ngIf="
                        getHighlightItemContent(item) &&
                        !isRichText(getHighlightItemContent(item))
                      "
                      class="highlight-text whitespace-pre-line"
                    >
                      {{ getHighlightItemContent(item) }}
                    </span>

                    <div
                      *ngIf="isRichText(getHighlightItemContent(item))"
                      class="highlight-text rich-text-content"
                      [innerHTML]="getHighlightItemContent(item) | richText"
                    ></div>
                  </div>
                </div>
              </div>

              <div
                *ngIf="
                  block.jsonData.length === 0 &&
                  hasBlockContent(block)
                "
                class="normal-paragraph rich-text-content"
                [innerHTML]="getContent(block) | richText"
              ></div>
            </ng-container>

            <!-- Block Type 2: Risk/List -->
            <ng-container
              *ngIf="isBlockType(block, 2)"
            >
              <ul
                *ngIf="hasBullets(block)"
                class="report-list risk-list"
              >
                <li
                  *ngFor="
                    let bullet of
                      getBullets(block)
                  "
                >
                  {{ bullet }}
                </li>
              </ul>

              <div
                *ngIf="
                  !hasBullets(block) &&
                  hasBlockContent(block)
                "
                class="normal-paragraph rich-text-content"
                [innerHTML]="getContent(block) | richText"
              ></div>
            </ng-container>

            <!-- Block Type 1: Paragraph -->
            <div
              *ngIf="
                isBlockType(block, 1) &&
                hasBlockContent(block)
              "
              class="normal-paragraph rich-text-content"
              [innerHTML]="getContent(block) | richText"
            ></div>

            <!-- Unknown Type Fallback -->
            <div
              *ngIf="
                !isBlockType(block, 1) &&
                !isBlockType(block, 2) &&
                !isBlockType(block, 3) &&
                hasBlockContent(block)
              "
              class="normal-paragraph rich-text-content"
              [innerHTML]="getContent(block) | richText"
            ></div>
          </ng-container>
        </ng-template>
      </ng-template>
    </ng-template>
        </ng-template>
      </ng-template>
    </ng-template>

    <!-- MRC / BRC signature page under Summary Terms and Conditions (as in the Word report). -->
    @if (+section.refValSeq === summaryTermsRefValSeq) {
      <div class="recommendation-signatures">
        @for (block of recommendationSignatureBlocks; track $index) {
          @if (block.caption) {
            <p class="signature-caption">{{ block.caption }}</p>
          }
          <div
            class="signature-row"
            [class.signature-row-three]="block.signatories.length === 3"
            [class.signature-row-four]="block.signatories.length === 4"
          >
            @for (signatory of block.signatories; track $index) {
              <div class="signatory">
                <div class="signatory-name">{{ signatory.name }}</div>
                @for (title of signatory.titleLines; track $index) {
                  <div>{{ title }}</div>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  </section>

  <!-- ====================================================== -->
  <!-- PROFILE OF BOARD OF DIRECTORS — Annexure-III (a),        -->
  <!-- native table shown immediately before Financial Analysis -->
  <!-- (sourced from the onboarding corporate-persons/committee  -->
  <!-- APIs).                                                    -->
  <!-- ====================================================== -->

  <section
    *ngIf="boardOfDirectors.length > 0"
    class="report-section"
  >
    <div class="annexure-label">Annexure-III (a)</div>
    <h2 class="section-title">{{ 'CREDIT_REPORT.BOARD_OF_DIRECTORS' | translate }}</h2>

    <div class="table-wrapper overflow-x-auto">
      <table class="financial-analysis-report-table board-of-directors-table">
        <thead>
          <tr>
            <th class="board-sr">S.#</th>
            <th>{{ 'CREDIT_REPORT.NAME_POSITION' | translate }}</th>
            <th>{{ 'CREDIT_REPORT.QUALIFICATION' | translate }}</th>
            <th>{{ 'CREDIT_REPORT.COMMITTEE_MEMBER' | translate }}</th>
            <th>{{ 'CREDIT_REPORT.EXPERIENCE_CURRENT_POSITION' | translate }}</th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let director of boardOfDirectors; let i = index"
          >
            <td class="board-sr">{{ i + 1 }}</td>
            <td>
              <div class="board-name">{{ director.name || '-' }}</div>
              <div class="board-position" *ngIf="director.position">
                ({{ director.position }})
              </div>
            </td>
            <td>{{ director.qualification || '-' }}</td>
            <td>{{ director.committee || '-' }}</td>
            <td class="board-experience">{{ director.experience || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <!-- ====================================================== -->
  <!-- LIST OF KEY MANAGEMENT — Annexure-III (b), native table  -->
  <!-- shown immediately after Board of Directors.              -->
  <!-- ====================================================== -->

  <section
    *ngIf="keyManagement.length > 0"
    class="report-section"
  >
    <div class="annexure-label">Annexure-III (b)</div>
    <h2 class="section-title">{{ 'CREDIT_REPORT.KEY_MANAGEMENT' | translate }}</h2>

    <div class="table-wrapper overflow-x-auto">
      <table class="financial-analysis-report-table key-management-table">
        <thead>
          <tr>
            <th>{{ 'CREDIT_REPORT.POSITION' | translate }}</th>
            <th>{{ 'COMMON.NAME' | translate }}</th>
            <th>{{ 'CREDIT_REPORT.QUALIFICATION' | translate }}</th>
            <th>{{ 'CREDIT_REPORT.EXPERIENCE_BRIEF' | translate }}</th>
          </tr>
        </thead>

        <tbody>
          <tr *ngFor="let member of keyManagement">
            <td class="km-position">{{ member.position || '-' }}</td>
            <td>{{ member.name || '-' }}</td>
            <td>{{ member.qualification || '-' }}</td>
            <td class="km-experience">{{ member.experience || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <!-- ====================================================== -->
  <!-- COMMITTEE DETAILS — Annexure-III (c), shown after Key    -->
  <!-- Management (same data as the Word document).             -->
  <!-- ====================================================== -->

  @if (committees.length > 0) {
    <section class="report-section">
      <div class="annexure-label">Annexure-III (c)</div>
      <h2 class="section-title">{{ 'CREDIT_REPORT.COMMITTEE_DETAILS' | translate }}</h2>

      <div class="table-wrapper overflow-x-auto">
        <table class="financial-analysis-report-table">
          <thead>
            <tr>
              <th class="board-sr">S.#</th>
              <th>{{ 'CREDIT_REPORT.COMMITTEE' | translate }}</th>
              <th>{{ 'CREDIT_REPORT.MEMBER_NAME' | translate }}</th>
              <th>{{ 'CREDIT_REPORT.POSITION' | translate }}</th>
              <th>{{ 'CREDIT_REPORT.QUALIFICATION' | translate }}</th>
            </tr>
          </thead>

          <tbody>
            @for (committee of committees; track $index; let committeeIndex = $index) {
              @if (committee.members.length > 0) {
                @for (member of committee.members; track $index; let first = $first) {
                  <tr>
                    @if (first) {
                      <td class="board-sr" [attr.rowspan]="committee.members.length">
                        {{ committeeIndex + 1 }}
                      </td>
                      <td class="km-position" [attr.rowspan]="committee.members.length">
                        {{ committee.committee || '-' }}
                      </td>
                    }
                    <td>{{ member.name || '-' }}</td>
                    <td>{{ member.position || '-' }}</td>
                    <td>{{ member.qualification || '-' }}</td>
                  </tr>
                }
              } @else {
                <tr>
                  <td class="board-sr">{{ committeeIndex + 1 }}</td>
                  <td class="km-position">{{ committee.committee || '-' }}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  <!-- ====================================================== -->
  <!-- ORGANOGRAM — Annexure-III (d), the image uploaded on    -->
  <!-- the Credit Proposal's Documents tab.                    -->
  <!-- ====================================================== -->

  @if (organogram) {
    <section class="report-section">
      <div class="annexure-label">Annexure-III (d)</div>
      <h2 class="section-title report-centered-title">{{ 'CREDIT_REPORT.ORGANOGRAM' | translate }}</h2>

      <img class="report-document-image" [src]="organogram.dataUrl" [alt]="'CREDIT_REPORT.ORGANOGRAM' | translate" />
    </section>
  }

  <!-- ====================================================== -->
  <!-- ATTACHMENTS — Attachments tab uploads: images shown,    -->
  <!-- other files (PDF, Word, Excel, ...) listed by name.     -->
  <!-- ====================================================== -->

  @if (attachments.length > 0) {
    <section class="report-section">
      <h2 class="section-title report-centered-title">{{ 'CREDIT_REPORT.ATTACHMENTS' | translate }}</h2>

      @for (attachment of attachments; track $index; let attachmentIndex = $index) {
        <div class="report-attachment">
          <h3 class="report-attachment-title">
            {{ attachmentIndex + 1 }}. {{ attachment.title || attachment.fileName }}
          </h3>

          @if (attachment.image) {
            <img class="report-document-image" [src]="attachment.image.dataUrl" [alt]="attachment.title" />
          } @else {
            <p class="report-attachment-file">
              {{ 'CREDIT_REPORT.ATTACHMENT_FILE' | translate: { name: attachment.fileName || '-' } }}
            </p>
          }
        </div>
      }
    </section>
  }

  <!-- ====================================================== -->
  <!-- FINANCIAL ANALYSIS — separate report section, rendered  -->
  <!-- last (immediately after the Tentative Repayment Plan   -->
  <!-- section). Independent of the section/block pipeline —  -->
  <!-- sourced from GET /crdt-prpsl/get-fnc-anlsys.            -->
  <!-- ====================================================== -->

  <section
    *ngIf="financialAnalysisSummaryRows.length > 0"
    class="report-section"
  >
    <h2 class="section-title">
      @if (sectionHeadingLabels.financialAnalysis) {
        {{ sectionHeadingLabels.financialAnalysis }} {{ 'CREDIT_REPORT.FINANCIAL_ANALYSIS' | translate }}:
      } @else {
        {{ 'CREDIT_REPORT.FINANCIAL_ANALYSIS' | translate }}
      }
    </h2>

    <div class="table-wrapper overflow-x-auto">
      <table class="financial-analysis-report-table financial-analysis-summary-table" dir="ltr" data-export-table="financial-analysis-summary">
        <thead>
          <tr>
            <th>
              <!-- Shown only in the exported (compact) snapshot, like the
                   institution name in the reference report's header cell. -->
              <span class="fa-export-institution">{{
                financialAnalysisSummaryRows[0]?.institution
              }}</span>
            </th>
            <th
              *ngFor="
                let period of financialAnalysisSummaryColumns;
                trackBy: trackByRepaymentColumn
              "
              class="text-end"
            >
              {{ period }}
            </th>
          </tr>
        </thead>

        <tbody>
          <ng-container
            *ngFor="
              let row of financialAnalysisSummaryRows;
              let rowIndex = index
            "
          >
            <tr
              *ngIf="
                row.head &&
                isFirstFinancialAnalysisHeadRow(
                  financialAnalysisSummaryRows,
                  rowIndex
                ) &&
                !isFinancialAnalysisTotalGroupRow(
                  financialAnalysisSummaryRows,
                  rowIndex
                )
              "
              class="head-row"
            >
              <td
                [attr.colspan]="financialAnalysisSummaryColumns.length + 1"
                class="head-cell"
              >
                {{ row.head }}
              </td>
            </tr>

            <!-- A "Total ..." head with a single row is shown as one bold
                 total line (label + values) instead of a heading row plus
                 an unlabeled value row. -->
            <tr
              [class.subtotal-row]="
                isFinancialAnalysisSubtotalRow(row) ||
                isFinancialAnalysisTotalGroupRow(
                  financialAnalysisSummaryRows,
                  rowIndex
                )
              "
            >
              <td
                [class.indent-cell]="
                  !isFinancialAnalysisSubtotalRow(row) &&
                  !isFinancialAnalysisTotalGroupRow(
                    financialAnalysisSummaryRows,
                    rowIndex
                  )
                "
              >
                {{
                  isFinancialAnalysisTotalGroupRow(
                    financialAnalysisSummaryRows,
                    rowIndex
                  )
                    ? (row.dscr && row.dscr.trim() !== '-' ? row.dscr : row.head)
                    : row.dscr || '-'
                }}
              </td>

              <td
                *ngFor="
                  let period of financialAnalysisSummaryColumns;
                  trackBy: trackByRepaymentColumn
                "
                class="text-end"
              >
                <ng-container
                  *ngIf="
                    getFinancialAnalysisPeriodValue(row, period) !== null;
                    else noFinancialAnalysisSummaryValue
                  "
                >
                  {{
                    getFinancialAnalysisPeriodValue(row, period)
                      | number: '1.0-2'
                  }}
                </ng-container>

                <ng-template #noFinancialAnalysisSummaryValue>
                  -
                </ng-template>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </section>
</div>

<!-- ====================================================== -->
<!-- NO DATA -->
<!-- ====================================================== -->

<ng-template #noData>
  <div
    *ngIf="!loading && !errorMessage"
    class="no-data-container"
  >
    <div class="no-data-icon">
      📄
    </div>

    <h3>
      {{ 'CREDIT_REPORT.NO_DATA_TITLE' | translate }}
    </h3>

    <p>
      {{ 'CREDIT_REPORT.NO_DATA_TEXT' | translate }}
    </p>
  </div>
</ng-template>